import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  LineChart, Line, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';
import api from '../services/api';

const fmt = (v) => v != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v) : '—';
const fmtPct = (v) => v != null ? `${(v * 100).toFixed(1)} %` : '—';
const fmtK = (v) => v != null ? (v >= 1000000 ? `${(v / 1000000).toFixed(1)}M€` : v >= 1000 ? `${(v / 1000).toFixed(0)}k€` : `${v}€`) : '—';

function KpiCard({ label, value, sub, evolution, color }) {
  const evo = evolution != null ? (evolution * 100).toFixed(1) : null;
  return (
    <div className="kpi-card" style={{ borderTop: `3px solid ${color || 'var(--ecf-red)'}` }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ color: color || 'var(--ecf-black)' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--ecf-grey)', marginTop: 4 }}>{sub}</div>}
      {evo != null && (
        <div className={`kpi-evolution ${parseFloat(evo) >= 0 ? 'positive' : 'negative'}`}>
          {parseFloat(evo) >= 0 ? '▲' : '▼'} {Math.abs(evo)}% vs N-1
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats]             = useState(null);
  const [adherents, setAdherents]     = useState([]);
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [indicateurs, setIndicateurs] = useState([]);
  const [tranches, setTranches]       = useState([]);
  const [benchmark, setBenchmark]     = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => { loadInit(); }, []);

  const loadInit = async () => {
    try {
      if (user.role === 'ADMIN') {
        const { data } = await api.get('/admin/stats');
        setStats(data);
      }
      if (user.role === 'GESTIONNAIRE' || user.role === 'ADMIN') {
        const { data } = await api.get('/adherents/');
        setAdherents(data);
        if (data.length > 0) loadIndicateurs(data[0].id);
      } else {
        // ADHÉRENT — charger ses propres indicateurs
        const { data: adhs } = await api.get('/adherents/').catch(() => ({ data: [] }));
        if (adhs.length > 0) {
          setSelectedAdherent(adhs[0]);
          loadIndicateurs(adhs[0].id);
        }
      }
      const { data: t } = await api.get('/configuration/tranches');
      setTranches(t);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const loadIndicateurs = async (adherentId) => {
    try {
      const { data } = await api.get(`/indicateurs/adherent/${adherentId}`);
      setIndicateurs(data.sort((a, b) => a.exercice - b.exercice));
      // Charger benchmark pour le dernier exercice
      if (data.length > 0) {
        const last = data[data.length - 1];
        if (last.tranche_ca_id) {
          const { data: bm } = await api.get(`/indicateurs/benchmark/${last.tranche_ca_id}?exercice=${last.exercice}`);
          setBenchmark(bm);
        }
      }
    } catch (e) {}
  };

  const handleSelectAdherent = (id) => {
    const adh = adherents.find(a => a.id === id);
    setSelectedAdherent(adh);
    setIndicateurs([]);
    setBenchmark(null);
    loadIndicateurs(id);
  };

  // Données pour les graphiques
  const chartCA = indicateurs.map(i => ({
    exercice: i.exercice,
    'CA': i.chiffre_affaires,
    'EBE': i.ebe,
    'Résultat net': i.resultat_net,
  }));

  const chartRatios = indicateurs.map(i => ({
    exercice: i.exercice,
    'EBE/CA': i.taux_ebe != null ? +(i.taux_ebe * 100).toFixed(1) : null,
    'Masse sal./CA': i.ratio_masse_salariale != null ? +(i.ratio_masse_salariale * 100).toFixed(1) : null,
    'Charges ext./CA': i.ratio_charges_externes != null ? +(i.ratio_charges_externes * 100).toFixed(1) : null,
  }));

  const lastInd = indicateurs[indicateurs.length - 1];

  // Données radar benchmark
  const radarData = benchmark?.benchmark ? [
    { subject: 'EBE/CA', moi: lastInd?.taux_ebe != null ? +(lastInd.taux_ebe * 100).toFixed(1) : 0, mediane: benchmark.benchmark.taux_ebe?.mediane != null ? +(benchmark.benchmark.taux_ebe.mediane * 100).toFixed(1) : 0 },
    { subject: 'Résultat/CA', moi: lastInd?.taux_resultat_net != null ? +(lastInd.taux_resultat_net * 100).toFixed(1) : 0, mediane: benchmark.benchmark.taux_resultat_net?.mediane != null ? +(benchmark.benchmark.taux_resultat_net.mediane * 100).toFixed(1) : 0 },
    { subject: 'Endettement', moi: lastInd?.ratio_endettement != null ? +lastInd.ratio_endettement.toFixed(2) : 0, mediane: benchmark.benchmark.ratio_endettement?.mediane != null ? +benchmark.benchmark.ratio_endettement.mediane.toFixed(2) : 0 },
    { subject: 'Masse sal.', moi: lastInd?.ratio_masse_salariale != null ? +(lastInd.ratio_masse_salariale * 100).toFixed(1) : 0, mediane: benchmark.benchmark.ratio_masse_salariale?.mediane != null ? +(benchmark.benchmark.ratio_masse_salariale.mediane * 100).toFixed(1) : 0 },
    { subject: 'Charges ext.', moi: lastInd?.ratio_charges_externes != null ? +(lastInd.ratio_charges_externes * 100).toFixed(1) : 0, mediane: benchmark.benchmark.ratio_charges_externes?.mediane != null ? +(benchmark.benchmark.ratio_charges_externes.mediane * 100).toFixed(1) : 0 },
  ] : [];

  if (loading) return <div><div className="topbar"><div className="topbar-title">Tableau de bord</div></div><div className="page-content"><p style={{ color: '#999' }}>Chargement...</p></div></div>;

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Tableau de bord</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {(user.role === 'GESTIONNAIRE' || user.role === 'ADMIN') && adherents.length > 0 && (
            <select className="form-input" style={{ margin: 0, width: 240, fontSize: '0.85rem' }}
              onChange={e => handleSelectAdherent(e.target.value)}
              defaultValue={adherents[0]?.id}>
              {adherents.map(a => <option key={a.id} value={a.id}>{a.nom_commercial}</option>)}
            </select>
          )}
          <span style={{ fontSize: '0.75rem', color: '#999', letterSpacing: 1 }}>
            {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
      </div>

      <div className="page-content">

        {/* KPI ADMIN */}
        {user.role === 'ADMIN' && stats && (
          <div className="kpi-grid" style={{ marginBottom: 24 }}>
            <KpiCard label="Adhérents actifs" value={stats.nb_adherents} color="var(--ecf-black)" />
            <KpiCard label="Structures" value={stats.nb_structures} color="var(--ecf-black)" />
            <KpiCard label="Bilans validés" value={stats.nb_bilans_valides} color="var(--ecf-success)" />
            <KpiCard label="À vérifier" value={stats.nb_bilans_attente}
              color={stats.nb_bilans_attente > 0 ? 'var(--ecf-warning)' : 'var(--ecf-black)'} />
          </div>
        )}

        {/* Pas de données */}
        {indicateurs.length === 0 && (
          <div className="card">
            <div className="card-title">Aucune donnée disponible</div>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              {user.role === 'ADHERENT'
                ? "Vos bilans apparaîtront ici dès qu'ils auront été importés et validés."
                : "Sélectionnez un adhérent ayant des bilans validés pour afficher les indicateurs."}
            </p>
          </div>
        )}

        {/* KPI indicateurs */}
        {lastInd && (
          <>
            <div className="kpi-grid" style={{ marginBottom: 20 }}>
              <KpiCard label="Chiffre d'affaires" value={fmtK(lastInd.chiffre_affaires)}
                sub={`Exercice ${lastInd.exercice}`} evolution={lastInd.evolution_ca} color="var(--ecf-red)" />
              <KpiCard label="EBE" value={fmtK(lastInd.ebe)}
                sub={`${fmtPct(lastInd.taux_ebe)} du CA`} color="var(--ecf-success)" />
              <KpiCard label="Résultat net" value={fmtK(lastInd.resultat_net)}
                sub={`${fmtPct(lastInd.taux_resultat_net)} du CA`}
                color={lastInd.resultat_net >= 0 ? 'var(--ecf-success)' : 'var(--ecf-error)'} />
              <KpiCard label="Trésorerie nette" value={fmtK(lastInd.tresorerie_nette)}
                color={lastInd.tresorerie_nette >= 0 ? 'var(--ecf-success)' : 'var(--ecf-error)'} />
              <KpiCard label="Fonds propres" value={fmtK(lastInd.fonds_propres)} color="var(--ecf-black)" />
              <KpiCard label="CAF" value={fmtK(lastInd.caf)} color="var(--ecf-black)" />
            </div>

            {/* Tranche CA */}
            {benchmark && (
              <div style={{ background: 'var(--ecf-black)', color: 'white', padding: '12px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, fontSize: '0.85rem' }}>
                <span style={{ background: 'var(--ecf-red)', padding: '2px 10px', fontFamily: 'var(--font-condensed)', fontWeight: 700, letterSpacing: 1 }}>TRANCHE</span>
                <span>{benchmark.tranche_libelle}</span>
                <span style={{ color: '#888' }}>|</span>
                <span><strong style={{ color: 'var(--ecf-red)' }}>{benchmark.nb_adherents}</strong> adhérents dans cette tranche</span>
                <span style={{ color: '#888' }}>|</span>
                <span style={{ color: '#888' }}>Exercice {benchmark.exercice}</span>
              </div>
            )}

            {/* Graphiques */}
            {chartCA.length > 1 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <div className="card">
                  <div className="card-title">Évolution CA / EBE / Résultat</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartCA} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="exercice" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={v => fmtK(v)} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v, n) => [fmt(v), n]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="CA" fill="var(--ecf-red)" />
                      <Bar dataKey="EBE" fill="var(--ecf-success)" />
                      <Bar dataKey="Résultat net" fill="var(--ecf-info, #1565C0)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="card">
                  <div className="card-title">Évolution des ratios (%)</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chartRatios} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="exercice" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v, n) => [`${v}%`, n]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="EBE/CA" stroke="var(--ecf-success)" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="Masse sal./CA" stroke="var(--ecf-red)" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="Charges ext./CA" stroke="#FF9800" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Benchmark radar */}
            {radarData.length > 0 && benchmark?.nb_adherents > 1 && (
              <div className="card">
                <div className="card-title">Benchmark réseau — Position vs médiane de la tranche (anonymisé)</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                      <Radar name="Vous" dataKey="moi" stroke="var(--ecf-red)" fill="var(--ecf-red)" fillOpacity={0.2} />
                      <Radar name="Médiane réseau" dataKey="mediane" stroke="#666" fill="#666" fillOpacity={0.1} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </RadarChart>
                  </ResponsiveContainer>

                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--ecf-grey)', marginBottom: 12, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700 }}>
                      Détail vs médiane réseau
                    </div>
                    {Object.entries(benchmark.benchmark).map(([key, stats]) => {
                      if (!stats || !lastInd) return null;
                      const labels = {
                        taux_ebe: 'EBE / CA',
                        taux_resultat_net: 'Résultat / CA',
                        ratio_masse_salariale: 'Masse salariale / CA',
                        ratio_charges_externes: 'Charges externes / CA',
                        ratio_endettement: 'Ratio d\'endettement',
                        capacite_remboursement: 'Capacité remboursement',
                      };
                      const moi = lastInd[key];
                      const med = stats.mediane;
                      if (moi == null || med == null) return null;
                      const isPct = key.startsWith('taux') || key.startsWith('ratio_masse') || key.startsWith('ratio_charges');
                      const diff = moi - med;
                      const better = key === 'ratio_endettement' || key === 'ratio_masse_salariale' || key === 'ratio_charges_externes' ? diff < 0 : diff > 0;
                      return (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--ecf-mid-grey)', fontSize: '0.82rem' }}>
                          <span style={{ color: 'var(--ecf-grey)' }}>{labels[key] || key}</span>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <span style={{ fontWeight: 700 }}>{isPct ? fmtPct(moi) : moi?.toFixed(2)}</span>
                            <span style={{ color: '#bbb' }}>/ méd. {isPct ? fmtPct(med) : med?.toFixed(2)}</span>
                            <span style={{ color: better ? 'var(--ecf-success)' : 'var(--ecf-error)', fontWeight: 700, fontSize: '0.75rem' }}>
                              {better ? '▲' : '▼'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Tableau récapitulatif multi-exercices */}
            {indicateurs.length > 1 && (
              <div className="card" style={{ padding: 0 }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ecf-mid-grey)' }}>
                  <div className="card-title" style={{ margin: 0 }}>Historique des indicateurs</div>
                </div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Indicateur</th>
                        {indicateurs.map(i => <th key={i.exercice} style={{ textAlign: 'right' }}>{i.exercice}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Chiffre d\'affaires', 'chiffre_affaires', fmt],
                        ['EBE', 'ebe', fmt],
                        ['EBE / CA', 'taux_ebe', fmtPct],
                        ['Résultat net', 'resultat_net', fmt],
                        ['Résultat net / CA', 'taux_resultat_net', fmtPct],
                        ['Masse salariale / CA', 'ratio_masse_salariale', fmtPct],
                        ['Charges ext. / CA', 'ratio_charges_externes', fmtPct],
                        ['Fonds propres', 'fonds_propres', fmt],
                        ['Trésorerie nette', 'tresorerie_nette', fmt],
                        ['Ratio endettement', 'ratio_endettement', v => v != null ? v.toFixed(2) : '—'],
                        ['CAF', 'caf', fmt],
                      ].map(([label, key, formatter]) => (
                        <tr key={key}>
                          <td style={{ fontWeight: 500, color: 'var(--ecf-grey)', fontSize: '0.82rem' }}>{label}</td>
                          {indicateurs.map(i => (
                            <td key={i.exercice} style={{ textAlign: 'right', fontWeight: 600 }}>
                              {formatter(i[key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
